import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ImageCropper from '../components/ImageCropper';
import { Loader2, LogOut, Save, Eye, CheckCircle, Plus, Trash2, Copy, Upload, CreditCard, Calendar, Download, AlertCircle, Lock, Mail, User, X } from 'lucide-react';
import { 
  cleanLinkedInHandle, 
  cleanXHandle, 
  cleanFacebookHandle, 
  cleanInstagramHandle, 
  cleanYouTubeHandle 
} from '../utils/socialMediaUtils';
import { formatPhoneInput } from '../utils/signature/formatUtils';
import {
  SIGNATURE_SIZE_OPTIONS,
  SIGNATURE_SIZE_PRESETS,
  BANNER_SIZE_OPTIONS,
  DEFAULT_BANNER_SIZE,
  getBannerPresetWidth,
  BANNER_WIDTH_MIN,
  BANNER_WIDTH_MAX,
} from '../utils/signature/sizeUtils';
import { SignatureSizeCompare } from '../components/signature/SignatureSizeCompare';

// Billing Tab Component
const BillingTab = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [companyId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const [subRes, invRes] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/invoices')
      ]);
      setSubscription(subRes.data);
      setInvoices(invRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePayment = async () => {
    try {
      setProcessing(true);
      const response = await api.post('/billing/create-portal-session');
      window.location.href = response.data.url;
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to open billing portal' });
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      await api.post('/billing/cancel');
      setMessage({ type: 'success', text: 'Subscription cancelled. You have access until the end of the billing period.' });
      setShowCancelDialog(false);
      fetchBillingData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to cancel subscription' });
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setProcessing(true);
      await api.post('/billing/reactivate');
      setMessage({ type: 'success', text: 'Subscription reactivated successfully!' });
      fetchBillingData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reactivate subscription' });
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePlan = async (newPlan) => {
    try {
      setProcessing(true);
      await api.post('/billing/change-plan', { new_plan: newPlan });
      setMessage({ type: 'success', text: 'Plan changed successfully! You will be charged the prorated amount.' });
      setShowUpgradeDialog(false);
      fetchBillingData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change plan' });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No active subscription found</p>
        </CardContent>
      </Card>
    );
  }

  const isTrialing = subscription.status === 'trial' || subscription.status === 'trialing';
  const isCancelled = subscription.status === 'cancelled' || subscription.status === 'grace_period';
  const isPersonal = subscription.plan_type === 'personal';

  return (
    <div className="space-y-6">
      {message && (
        <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Your subscription details and billing information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg">
            <div>
              <div className="flex items-center space-x-3">
                <h3 className="text-2xl font-bold capitalize">{subscription.plan_type} Plan</h3>
                {isTrialing && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                    15-Day Trial
                  </span>
                )}
                {isCancelled && (
                  <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
                    Cancelled
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-1">
                ${subscription.plan_type === 'personal' ? '9' : '59'}/month
              </p>
            </div>
            <CreditCard className="h-12 w-12 text-primary/30" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-sm text-gray-500">Status</Label>
              <p className="font-medium capitalize">
                {subscription.status === 'trialing' ? '15-Day Trial' : subscription.status.replace('_', ' ')}
              </p>
            </div>
            <div>
              <Label className="text-sm text-gray-500">
                {isTrialing ? 'Trial Ends' : 'Next Billing Date'}
              </Label>
              <p className="font-medium">
                {isTrialing 
                  ? new Date(subscription.trial_end).toLocaleDateString()
                  : new Date(subscription.current_period_end).toLocaleDateString()
                }
              </p>
            </div>
          </div>

          {isCancelled && subscription.grace_period_end && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Your subscription is cancelled. You have until {new Date(subscription.grace_period_end).toLocaleDateString()} to reactivate.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex space-x-3">
            {!isCancelled && (
              <>
                <Button onClick={handleManagePayment} disabled={processing} variant="outline">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage Payment Method
                </Button>
                {isPersonal && (
                  <Button onClick={() => setShowUpgradeDialog(true)} disabled={processing}>
                    Upgrade to Business
                  </Button>
                )}
              </>
            )}
            {isCancelled ? (
              <Button onClick={handleReactivate} disabled={processing}>
                {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Reactivate Subscription
              </Button>
            ) : (
              <Button 
                onClick={() => setShowCancelDialog(true)} 
                disabled={processing}
                variant="destructive"
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice History</CardTitle>
          <CardDescription>Download your past invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No invoices yet</p>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">
                      ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(invoice.created).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status}
                    </span>
                    {invoice.invoice_pdf && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4">
            <CardHeader>
              <CardTitle>Cancel Subscription?</CardTitle>
              <CardDescription>
                Are you sure you want to cancel? You'll have access until the end of your billing period.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  After cancellation, you'll have 30 days to reactivate before your data is deleted.
                </AlertDescription>
              </Alert>
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCancelDialog(false)}
                  className="flex-1"
                >
                  Keep Subscription
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  className="flex-1"
                >
                  {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Yes, Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upgrade Dialog */}
      {showUpgradeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4">
            <CardHeader>
              <CardTitle>Upgrade to Business?</CardTitle>
              <CardDescription>
                Get unlimited users, white label branding, and more!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">What you'll get:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✓ Unlimited users</li>
                  <li>✓ White label branding</li>
                  <li>✓ Multiple locations</li>
                  <li>✓ Advanced field controls</li>
                  <li>✓ Priority support</li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                You'll be charged the prorated difference immediately, then $59/month going forward.
              </p>
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowUpgradeDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleChangePlan('business')}
                  disabled={processing}
                  className="flex-1"
                >
                  {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Upgrade Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Account Tab Component
const AccountTab = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [emailData, setEmailData] = useState({
    new_email: '',
    password: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      await api.post('/profile/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setMessage(null);
      await api.post('/profile/change-email', emailData);
      setMessage({ type: 'success', text: 'Verification email sent! Please check your new email address.' });
      setEmailData({ new_email: '', password: '' });
      setShowEmailForm(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to request email change' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          {!showPasswordForm ? (
            <Button onClick={() => setShowPasswordForm(true)}>
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <div className="flex space-x-3">
                <Button type="submit" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Update Password
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowPasswordForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Change Email */}
      <Card>
        <CardHeader>
          <CardTitle>Change Email Address</CardTitle>
          <CardDescription>Update your account email (requires verification)</CardDescription>
        </CardHeader>
        <CardContent>
          {!showEmailForm ? (
            <Button onClick={() => setShowEmailForm(true)}>
              <Mail className="h-4 w-4 mr-2" />
              Change Email
            </Button>
          ) : (
            <form onSubmit={handleEmailChange} className="space-y-4">
              <div>
                <Label htmlFor="new_email">New Email Address</Label>
                <Input
                  id="new_email"
                  type="email"
                  value={emailData.new_email}
                  onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password_confirm">Confirm Password</Label>
                <Input
                  id="password_confirm"
                  type="password"
                  value={emailData.password}
                  onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                  required
                />
              </div>
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  You'll receive a verification link at your new email address. Your email won't change until you verify it.
                </AlertDescription>
              </Alert>
              <div className="flex space-x-3">
                <Button type="submit" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Send Verification Email
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEmailForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CompanySettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [config, setConfig] = useState({});
  const [bannerWidthDraft, setBannerWidthDraft] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const applySignatureSize = (value) => setConfig((prev) => ({
    ...prev,
    signature_size: value,
    banner_width: prev.banner_width
      ? Math.min(prev.banner_width, SIGNATURE_SIZE_PRESETS[value].maxWidth)
      : prev.banner_width,
  }));
  const applyBannerSize = (value) => {
    setBannerWidthDraft(null);
    setConfig((prev) => ({ ...prev, banner_size: value, banner_width: null }));
  };  const sizePreset = SIGNATURE_SIZE_PRESETS[config.signature_size] || SIGNATURE_SIZE_PRESETS.standard;
  const defaultBannerWidth = getBannerPresetWidth(config.signature_size, config.banner_size);
  const maxBannerWidth = Math.min(BANNER_WIDTH_MAX, sizePreset.maxWidth);
  const clampBannerWidth = (value) =>
    Math.min(Math.max(Number(value) || BANNER_WIDTH_MIN, BANNER_WIDTH_MIN), maxBannerWidth);
  const [locations, setLocations] = useState([]);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isMountedRef = useRef(true);
  const contentRef = useRef(null);
  
  // Image cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropperConfig, setCropperConfig] = useState({
    type: 'logo',
    aspectRatio: 1,
    recommendedSize: { width: 512, height: 512 },
    allowFreeform: false
  });
  
  // New location form state
  const [newLocation, setNewLocation] = useState({
    location_id: '',
    name: '',
    address: '',
    address_link: '',
    phone_number: '',
    fax_number: '',
    country_code: '+1'
  });

  // Field visibility and editability (matching SignatureTool field names)
  const fields = [
    'fullName', 'credentials', 'jobTitle', 'company', 'department', 'countryCode',
    'phoneNumber', 'mobileNumber', 'faxNumber', 'email', 'website', 'address',
    'addressLink', 'location', 'bookMeeting', 'bookMeetingText', 'profileImage',
    'companyLogo', 'companyLogoLink', 'bannerImage', 'bannerLink', 'brandColor',
    'useBrandColorForSocial', 'linkedin', 'xUsername', 'facebook', 'instagram',
    'disclaimer', 'youtube'
  ];

  useEffect(() => {
    isMountedRef.current = true;
    
    // Check for masquerade token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const masqueradeId = urlParams.get('mid');
    
    if (masqueradeId) {
      // Get masquerade data from sessionStorage
      const masqueradeData = sessionStorage.getItem(masqueradeId);
      if (masqueradeData) {
        try {
          const { token, user: masqueradeUser } = JSON.parse(masqueradeData);
          // Apply masquerade credentials
          localStorage.setItem('auth_token', token);
          localStorage.setItem('user', JSON.stringify(masqueradeUser));
          // Clean up
          sessionStorage.removeItem(masqueradeId);
          // Remove mid from URL and reload
          window.history.replaceState({}, '', '/settings');
          window.location.reload();
          return;
        } catch (error) {
          console.error('Failed to apply masquerade:', error);
        }
      }
    }
    
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    
    fetchCompanyData();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [user, navigate]);

  const fetchCompanyData = async () => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const [companyRes, configRes] = await Promise.all([
        api.get(`/companies/${user.company_id}`),
        api.get(`/companies/${user.company_id}/config`),
      ]);
      if (isMountedRef.current) {
        setCompany(companyRes.data);
        setConfig(configRes.data || {});
        setLocations(companyRes.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSaveConfig = async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setSaving(true);
      await api.put(`/companies/${user.company_id}/config`, config);
      if (isMountedRef.current) {
        setSuccess(true);
        setTimeout(() => {
          if (isMountedRef.current) setSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      if (isMountedRef.current) {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Error saving configuration: ${errorMsg}\n\nPlease check console for details.`);
      }
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const handleAddLocation = async () => {
    if (!isMountedRef.current) return;
    if (!newLocation.location_id || !newLocation.name || !newLocation.address) {
      alert('Please fill in Location ID, Name, and Address');
      return;
    }
    
    try {
      await api.post(`/companies/${user.company_id}/locations`, newLocation);
      if (!isMountedRef.current) return;
      await fetchCompanyData();
      if (!isMountedRef.current) return;
      // Reset form
      setNewLocation({
        location_id: '',
        name: '',
        address: '',
        address_link: '',
        phone_number: '',
        fax_number: '',
        country_code: '+1'
      });
      setSuccess(true);
      setTimeout(() => {
        if (isMountedRef.current) setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error adding location:', error);
      if (isMountedRef.current) {
        alert('Error adding location. Please try again.');
      }
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!isMountedRef.current) return;
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }
    
    try {
      await api.delete(`/companies/${user.company_id}/locations/${locationId}`);
      if (!isMountedRef.current) return;
      await fetchCompanyData();
      if (!isMountedRef.current) return;
      setSuccess(true);
      setTimeout(() => {
        if (isMountedRef.current) setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error deleting location:', error);
      if (isMountedRef.current) {
        alert('Error deleting location. Please try again.');
      }
    }
  };

  const handleImageUpload = async (event, type) => {
    if (!isMountedRef.current) return;
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Read file and open cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      
      // Set cropper configuration based on image type
      if (type === 'logo') {
        setCropperConfig({
          type: 'logo',
          aspectRatio: 1, // Default square but allow adjustment
          recommendedSize: { width: 200, height: 100 }, // Support horizontal logos
          allowFreeform: true // Allow any aspect ratio for logo
        });
      } else if (type === 'banner') {
        setCropperConfig({
          type: 'banner',
          aspectRatio: 16 / 3, // Default aspect ratio
          recommendedSize: { width: 1600, height: 600 }, // Max width 1600px, height up to 600px
          allowFreeform: true // Allow any aspect ratio - short or tall banners
        });
      } else if (type === 'default_profile') {
        setCropperConfig({
          type: 'default_profile',
          aspectRatio: 1, // Square
          recommendedSize: { width: 200, height: 200 },
          allowFreeform: false
        });
      }
      
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    
    // Reset input
    event.target.value = '';
  };

  const handleCropComplete = async (croppedBlob, format) => {
    if (!isMountedRef.current) return;
    
    try {
      if (isMountedRef.current) setUploading(true);
      
      // Create a file from the cropped blob with correct extension
      const extension = format === 'image/png' ? 'png' : 'jpg';
      const croppedFile = new File(
        [croppedBlob], 
        `${cropperConfig.type}_${Date.now()}.${extension}`,
        { type: format }
      );
      
      const formData = new FormData();
      formData.append('file', croppedFile);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!isMountedRef.current) return;
      
      if (cropperConfig.type === 'logo') {
        setConfig({ ...config, company_logo: response.data.url });
      } else if (cropperConfig.type === 'banner') {
        setConfig({ ...config, banner_image: response.data.url });
      } else if (cropperConfig.type === 'default_profile') {
        setConfig({ ...config, default_profile_image: response.data.url });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      if (isMountedRef.current) {
        alert('Error uploading image. Please try again.');
      }
    } finally {
      if (isMountedRef.current) setUploading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!isMountedRef.current) return;
    navigator.clipboard.writeText(text).catch(err => {
      console.error('Failed to copy:', err);
    });
    setSuccess(true);
    setTimeout(() => {
      if (isMountedRef.current) setSuccess(false);
    }, 2000);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Convert camelCase to Title Case for display
  const formatFieldName = (field) => {
    // Convert camelCase to space-separated words
    return field.replace(/([A-Z])/g, ' $1').trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Convert camelCase to snake_case for API storage
  const toSnakeCase = (str) => {
    return str.replace(/([A-Z])/g, '_$1').toLowerCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Share URL */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">Share with Your Team</h2>
              <p className="text-gray-300 mb-3">Share this URL with your team members to let them create their email signatures:</p>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-800 text-gray-200 px-4 py-3 rounded-lg font-mono text-sm break-all">
                  https://slicksignature.com/signature/{company?.signature_url}
                </div>
                <Button
                  onClick={() => copyToClipboard(`https://slicksignature.com/signature/${company?.signature_url}`)}
                  variant="secondary"
                  className="bg-white text-black hover:bg-gray-200"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-black shadow-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" alt="SlickSignature" className="h-12" />
              <span className="text-sm text-gray-400">{user?.first_name} {user?.last_name}</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white hover:bg-gray-800"
                onClick={() => window.open(`/signature/${company?.signature_url}?preview=true`, '_blank')}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview as Employee
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-white border-white hover:bg-gray-800"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Tabs */}
      <div ref={contentRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Alert at Top - Fixed Position */}
        {success && (
          <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
            <Alert className="mb-6 border-green-200 bg-green-50 shadow-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-800 font-semibold">
                Changes saved successfully!
              </AlertDescription>
            </Alert>
          </div>
        )}
        
        <Tabs defaultValue="company-info" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="company-info">Company Info</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            <TabsTrigger value="field-controls">Field Controls</TabsTrigger>
            <TabsTrigger value="white-label">White Label</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="account">Account</TabsTrigger>
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="company-info">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>
                  These settings will be applied to all signatures generated with this tool.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input value={company?.name || ''} disabled className="bg-gray-50" />
                  </div>

                  <div>
                    <Label>Website</Label>
                    <Input
                      value={config.website || company?.website || ''}
                      onChange={(e) => setConfig({ ...config, website: e.target.value })}
                      placeholder="slicksignature.com"
                    />
                  </div>

                  <div>
                    <Label>Default Meeting Link Text</Label>
                    <Input
                      value={config.meeting_link_text || 'Book a Meeting'}
                      onChange={(e) => setConfig({ ...config, meeting_link_text: e.target.value })}
                      placeholder="Book a Meeting"
                    />
                  </div>

                  <div>
                    <Label>Email Disclaimer</Label>
                    <Textarea
                      value={config.disclaimer || ''}
                      onChange={(e) => setConfig({ ...config, disclaimer: e.target.value })}
                      placeholder="Confidentiality notice or legal disclaimer"
                      rows={4}
                    />
                  </div>

                  {/* Placeholder/Demo Data Section */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-medium mb-2">Demo/Placeholder Data</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Set default values that will pre-populate the signature tool for employees. Users can override these values.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Default Full Name</Label>
                        <Input
                          value={config.default_full_name || ''}
                          onChange={(e) => setConfig({ ...config, default_full_name: e.target.value })}
                          placeholder="John Smith"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Credentials</Label>
                        <Input
                          value={config.default_credentials || ''}
                          onChange={(e) => setConfig({ ...config, default_credentials: e.target.value })}
                          placeholder="MBA, CPA"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Job Title</Label>
                        <Input
                          value={config.default_job_title || ''}
                          onChange={(e) => setConfig({ ...config, default_job_title: e.target.value })}
                          placeholder="Marketing Director"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Email</Label>
                        <Input
                          value={config.default_email || ''}
                          onChange={(e) => setConfig({ ...config, default_email: e.target.value })}
                          placeholder="name@company.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Phone</Label>
                        <Input
                          value={config.default_phone_number || ''}
                          onChange={(e) => setConfig({ ...config, default_phone_number: formatPhoneInput(e.target.value) })}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                          maxLength={14}
                        />
                      </div>

                      <div>
                        <Label className="text-sm">Default Fax</Label>
                        <Input
                          value={config.default_fax_number || ''}
                          onChange={(e) => setConfig({ ...config, default_fax_number: formatPhoneInput(e.target.value) })}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                          maxLength={14}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default LinkedIn</Label>
                        <Input
                          value={config.default_linkedin || ''}
                          onChange={(e) => setConfig({ ...config, default_linkedin: cleanLinkedInHandle(e.target.value) })}
                          placeholder="username"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default X (Twitter)</Label>
                        <Input
                          value={config.default_x_username || ''}
                          onChange={(e) => setConfig({ ...config, default_x_username: cleanXHandle(e.target.value) })}
                          placeholder="username"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Facebook</Label>
                        <Input
                          value={config.default_facebook || ''}
                          onChange={(e) => setConfig({ ...config, default_facebook: cleanFacebookHandle(e.target.value) })}
                          placeholder="username or page"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default Instagram</Label>
                        <Input
                          value={config.default_instagram || ''}
                          onChange={(e) => setConfig({ ...config, default_instagram: cleanInstagramHandle(e.target.value) })}
                          placeholder="username"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Default YouTube</Label>
                        <Input
                          value={config.default_youtube || ''}
                          onChange={(e) => setConfig({ ...config, default_youtube: cleanYouTubeHandle(e.target.value) })}
                          placeholder="channel"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end border-t pt-4 mt-6">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Branding</CardTitle>
                <CardDescription>
                  Customize the visual appearance of your email signatures
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="pb-4 border-b">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Signature Size</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCompareOpen(true)}
                        data-testid="compare-sizes-button"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Compare sizes
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 mb-3">
                      Scales the text, profile photo, logo and banner for everyone in your company.
                      Sizes are written as fixed pixel values so they render the same in Outlook, Gmail and Apple Mail.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Signature size">
                      {SIGNATURE_SIZE_OPTIONS.map((option) => {
                        const active = (config.signature_size || 'standard') === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            data-testid={`signature-size-${option.value}`}
                            role="radio"
                            aria-checked={active}
                            tabIndex={active ? 0 : -1}
                            onKeyDown={(e) => {
                              if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
                              e.preventDefault();
                              const options = SIGNATURE_SIZE_OPTIONS.map((o) => o.value);
                              const current = options.indexOf(config.signature_size || 'standard');
                              const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
                              const next = options[(current + step + options.length) % options.length];
                              applySignatureSize(next);
                            }}
                            onClick={() => applySignatureSize(option.value)}
                            className={`text-left rounded-lg border p-3 transition-colors ${
                              active
                                ? 'border-[#EB2F5B] bg-pink-50 ring-1 ring-[#EB2F5B]'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="block font-medium text-gray-900">{option.label}</span>
                            <span className="block text-xs text-gray-500 mt-1">{option.description}</span>
                          </button>
                        );
                      })}
                    </div>
                    <SignatureSizeCompare
                      open={compareOpen}
                      onOpenChange={setCompareOpen}
                      config={config}
                      company={company}
                      currentSize={config.signature_size || 'standard'}
                      currentBannerSize={config.banner_size || DEFAULT_BANNER_SIZE}
                      onApply={(value, bannerSize) => {
                        applySignatureSize(value);
                        applyBannerSize(bannerSize);
                        setCompareOpen(false);
                      }}
                    />
                  </div>

                  <div className="pb-4 border-b">
                    <Label>Banner Size</Label>
                    <p className="text-sm text-gray-500 mt-1 mb-3">
                      Scales the banner image at the bottom of the signature. Pick Small to keep the whole signature tight.
                    </p>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Banner size">
                      {BANNER_SIZE_OPTIONS.map((option) => {
                        const active = (config.banner_size || DEFAULT_BANNER_SIZE) === option.value && !config.banner_width;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            tabIndex={active ? 0 : -1}
                            data-testid={`banner-size-${option.value}`}
                            onKeyDown={(e) => {
                              if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
                              e.preventDefault();
                              const values = BANNER_SIZE_OPTIONS.map((o) => o.value);
                              const current = values.indexOf(config.banner_size || DEFAULT_BANNER_SIZE);
                              const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
                              applyBannerSize(values[(current + step + values.length) % values.length]);
                            }}
                            onClick={() => applyBannerSize(option.value)}
                            className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                              active
                                ? 'border-[#EB2F5B] bg-pink-50 ring-1 ring-[#EB2F5B] text-gray-900'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            }`}
                          >
                            {option.label}
                            <span className="block text-xs text-gray-500">
                              {getBannerPresetWidth(config.signature_size, option.value)}px
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {config.banner_width ? (
                      <p className="text-xs text-gray-500 mt-2" data-testid="banner-size-custom-note">
                        Using a custom width of {config.banner_width}px — pick a size above to go back to the presets.
                      </p>
                    ) : null}
                  </div>

                  <div className="pb-4 border-b">
                    <Label>Banner Width (fine tune)</Label>
                    <p className="text-sm text-gray-500 mt-1 mb-3">
                      Overrides the banner image width. Lower this if your banner (badges, award logos) looks oversized.                    </p>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min={BANNER_WIDTH_MIN}
                        max={maxBannerWidth}
                        step={10}
                        value={config.banner_width || defaultBannerWidth}
                        onChange={(e) => {
                          setBannerWidthDraft(null);
                          setConfig({ ...config, banner_width: clampBannerWidth(e.target.value) });
                        }}
                        className="flex-1 accent-[#EB2F5B]"
                        data-testid="banner-width-slider"
                      />
                      <Input
                        type="number"
                        min={BANNER_WIDTH_MIN}
                        max={maxBannerWidth}
                        value={bannerWidthDraft ?? (config.banner_width || defaultBannerWidth)}
                        onChange={(e) => setBannerWidthDraft(e.target.value)}
                        onBlur={(e) => {
                          setConfig({ ...config, banner_width: clampBannerWidth(e.target.value) });
                          setBannerWidthDraft(null);
                        }}
                        className="w-24"
                        data-testid="banner-width-input"
                      />
                      <span className="text-sm text-gray-500">px (max {maxBannerWidth})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setBannerWidthDraft(null);
                          setConfig({ ...config, banner_width: null });
                        }}
                        data-testid="banner-width-reset"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>Brand Color</Label>
                    <div className="flex items-center space-x-3 mt-2">
                      <div 
                        className="w-16 h-16 rounded-lg border-2 border-gray-300" 
                        style={{ backgroundColor: config.brand_color || company?.brand_color || '#2563eb' }}
                      />
                      <div className="flex items-center space-x-2">
                        <Input
                          type="color"
                          value={config.brand_color || company?.brand_color || '#2563eb'}
                          onChange={(e) => setConfig({ ...config, brand_color: e.target.value })}
                          className="w-12 h-10 cursor-pointer"
                        />
                        <Input
                          value={config.brand_color || company?.brand_color || '#2563eb'}
                          onChange={(e) => setConfig({ ...config, brand_color: e.target.value })}
                          className="flex-1"
                          placeholder="#2563eb"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      This color will be applied to icons and job title in the signature.
                    </p>
                  </div>

                  <div className="flex items-center justify-between py-3 border-t">
                    <div>
                      <Label>Use brand color for social media icons</Label>
                      <p className="text-sm text-gray-500">
                        When ON: All social icons will use your brand color. When OFF: Social icons will use their original colors (LinkedIn blue, YouTube red, etc.)
                      </p>
                    </div>
                    <Switch
                      checked={config.use_brand_color_for_social_icons !== false}
                      onCheckedChange={(checked) =>
                        setConfig({ ...config, use_brand_color_for_social_icons: checked })
                      }
                    />
                  </div>

                  <div className="py-3 border-t">
                    <Label>Show field labels</Label>
                    <p className="text-sm text-gray-500 mb-3">
                      Labels are the prefixes before each line ("D:", "Email:", "Web:"). Choose which lines get one.
                    </p>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Field label scope">
                      {[
                        { value: 'all', label: 'All fields' },
                        { value: 'phones', label: 'Phone lines only' },
                        { value: 'none', label: 'None' },
                      ].map((option) => {
                        const active = (config.field_labels_scope || (config.show_field_labels === false ? 'none' : 'all')) === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            data-testid={`field-labels-scope-${option.value}`}
                            onClick={() => setConfig({
                              ...config,
                              field_labels_scope: option.value,
                              show_field_labels: option.value !== 'none',
                            })}
                            className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                              active
                                ? 'border-[#EB2F5B] bg-pink-50 ring-1 ring-[#EB2F5B] text-gray-900'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-t">
                    <div>
                      <Label>Show country code on phone numbers</Label>
                      <p className="text-sm text-gray-500">
                        When ON: numbers display as "+1 (555) 555-5555". When OFF: the country code is hidden, but the click-to-call link still works.
                      </p>
                    </div>
                    <Switch
                      checked={config.show_country_code !== false}
                      onCheckedChange={(checked) => setConfig({ ...config, show_country_code: checked })}
                      data-testid="show-country-code-switch"
                    />
                  </div>

                  <div className="py-3 border-t space-y-4">
                    <div>
                      <Label>Phone labels</Label>
                      <p className="text-sm text-gray-500">
                        Choose how the office and mobile lines are labelled. Pick "None" to leave that line unlabelled.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Office phone label</Label>
                        <select
                          value={config.office_label ?? 'Office'}
                          onChange={(e) => setConfig({ ...config, office_label: e.target.value })}
                          data-testid="office-label-select"
                          className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
                        >
                          <option value="Office">Office:</option>
                          <option value="D">D:</option>
                          <option value="O">O:</option>
                          <option value="Tel">Tel:</option>
                          <option value="">None</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Mobile phone label</Label>
                        <select
                          value={config.mobile_label ?? 'Mobile'}
                          onChange={(e) => setConfig({ ...config, mobile_label: e.target.value })}
                          data-testid="mobile-label-select"
                          className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
                        >
                          <option value="Mobile">Mobile:</option>
                          <option value="M">M:</option>
                          <option value="C">C:</option>
                          <option value="">None</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-t">
                    <div>
                      <Label>Match label style to the numbers</Label>
                      <p className="text-sm text-gray-500">
                        When ON: labels use the same colour, font and size as the value next to them. When OFF: labels are bold in your brand colour.
                      </p>
                    </div>
                    <Switch
                      checked={config.label_style === 'match'}
                      onCheckedChange={(checked) => setConfig({ ...config, label_style: checked ? 'match' : 'brand' })}
                      data-testid="label-style-switch"
                    />
                  </div>

                  {config.label_style === 'match' && (
                    <div className="py-3 border-t">
                      <Label>Apply matched style to</Label>
                      <div className="flex flex-wrap gap-2 mt-2" role="radiogroup" aria-label="Matched label scope">
                        {[
                          { value: 'phones', label: 'Phone lines only' },
                          { value: 'all', label: 'All fields' },
                        ].map((option) => {
                          const active = (config.label_style_scope || 'phones') === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              data-testid={`label-scope-${option.value}`}
                              onClick={() => setConfig({ ...config, label_style_scope: option.value })}
                              className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                                active
                                  ? 'border-[#EB2F5B] bg-pink-50 ring-1 ring-[#EB2F5B] text-gray-900'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}



                  <div className="border-t pt-6">
                    <Label className="flex items-center space-x-2">
                      <span>Company Logo</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Label>
                    <div className="mt-3 flex items-center space-x-4">
                      <div className="relative w-32 h-32 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                        {config.company_logo ? (
                          <>
                            <img src={config.company_logo} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                            <button
                              type="button"
                              data-testid="remove-company-logo-btn"
                              title="Remove logo"
                              onClick={() => setConfig({ ...config, company_logo: '' })}
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm">No logo</span>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <p className="font-medium mb-2">Upload company logo</p>
                          <label htmlFor="logo-upload" className="cursor-pointer">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors text-center">
                              <Upload className="mx-auto h-8 w-8 text-gray-400" />
                              <p className="mt-2 text-sm text-gray-600">Choose Logo</p>
                            </div>
                            <input
                              id="logo-upload"
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, 'logo')}
                              className="hidden"
                              disabled={uploading}
                            />
                          </label>
                          <p className="text-sm text-gray-500 mt-2">
                            Max width: 200px (height scales automatically). Supports square & horizontal logos. Max size: 10MB. PNG files preserve transparency.
                          </p>
                          <p className="text-sm text-blue-600 mt-1">
                            Note: Updating this logo will automatically update all signatures using it.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label>Logo Link URL</Label>
                      <Input
                        value={config.company_logo_link || 'https://slicksignature.com'}
                        onChange={(e) => setConfig({ ...config, company_logo_link: e.target.value })}
                        placeholder="https://slicksignature.com"
                        className="mt-2"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Enter the URL where users will be directed when they click on the logo.
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <Label className="flex items-center space-x-2">
                      <span>Banner Image</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Label>
                    <div className="mt-3 flex items-center space-x-4">
                      <div className="relative w-48 h-24 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                        {config.banner_image ? (
                          <>
                            <img src={config.banner_image} alt="Banner" className="max-w-full max-h-full object-contain" />
                            <button
                              type="button"
                              data-testid="remove-banner-image-btn"
                              title="Remove banner"
                              onClick={() => setConfig({ ...config, banner_image: '' })}
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm">No banner</span>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <p className="font-medium mb-2">Upload banner image</p>
                          <label htmlFor="banner-upload" className="cursor-pointer">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors text-center">
                              <Upload className="mx-auto h-8 w-8 text-gray-400" />
                              <p className="mt-2 text-sm text-gray-600">Choose Banner</p>
                            </div>
                            <input
                              id="banner-upload"
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, 'banner')}
                              className="hidden"
                              disabled={uploading}
                            />
                          </label>
                          <p className="text-sm text-gray-500 mt-2">
                            Max width: 1600px, max height: 600px (fully flexible). Max size: 10MB. PNG files preserve transparency.
                          </p>
                          <p className="text-sm text-blue-600 mt-1">
                            Note: Updating this banner will automatically update all signatures using it.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label>Banner Link URL</Label>
                      <Input
                        value={config.banner_link || 'https://example.com/promotion'}
                        onChange={(e) => setConfig({ ...config, banner_link: e.target.value })}
                        placeholder="https://example.com/promotion"
                        className="mt-2"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Enter the URL where users will be directed when they click on the banner.
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <Label className="flex items-center space-x-2">
                      <span>Default Profile Image</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      This image will be used as the default profile picture for new signatures
                    </p>
                    <div className="mt-3 flex items-center space-x-4">
                      <div className="relative w-32 h-32 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                        {config.default_profile_image ? (
                          <>
                            <img src={config.default_profile_image} alt="Default Profile" className="max-w-full max-h-full object-contain rounded-lg" />
                            <button
                              type="button"
                              data-testid="remove-default-profile-btn"
                              title="Remove default profile image"
                              onClick={() => setConfig({ ...config, default_profile_image: '' })}
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm">No default</span>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <p className="font-medium mb-2">Upload default profile image</p>
                          <label htmlFor="default-profile-upload" className="cursor-pointer">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors text-center">
                              <Upload className="mx-auto h-8 w-8 text-gray-400" />
                              <p className="mt-2 text-sm text-gray-600">Choose Image</p>
                            </div>
                            <input
                              id="default-profile-upload"
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, 'default_profile')}
                              className="hidden"
                              disabled={uploading}
                            />
                          </label>
                          <p className="text-sm text-gray-500 mt-2">
                            Recommended size: 200×200px (square). Max size: 10MB. PNG files preserve transparency.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Design Service Ad */}
                  <div className="border-t pt-6 mt-6">
                    <Card className="bg-gradient-to-br from-pink-50 to-purple-50 border-pink-200">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                              Need help with your logo and a custom banner design?
                            </h3>
                            <p className="text-gray-700 mb-4">
                              We are happy to help customize a banner and logo to your needs. Our professional designers will create stunning, on-brand visuals for your email signatures.
                            </p>
                            <div className="flex items-center gap-4">
                              <div className="text-2xl font-bold text-pink-600">$179</div>
                              <Button 
                                className="bg-pink-600 hover:bg-pink-700 text-white"
                                onClick={async () => {
                                  try {
                                    const response = await api.post('/stripe/create-design-service-checkout');
                                    window.location.href = response.data.url;
                                  } catch (error) {
                                    console.error('Failed to create checkout:', error);
                                    alert('Failed to start checkout. Please try again.');
                                  }
                                }}
                              >
                                Buy Now
                              </Button>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              One-time service • Logo adjustment & custom banner design
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="flex justify-end border-t pt-4 mt-6">
                  <Button onClick={handleSaveConfig} disabled={saving || uploading}>
                    {saving || uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {uploading ? 'Uploading...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <Card>
              <CardHeader>
                <CardTitle>Office Locations</CardTitle>
                <CardDescription>
                  Add multiple office locations that employees can select from.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Existing Locations */}
                {locations.length > 0 ? (
                  <div className="space-y-3">
                    {locations.map((location) => (
                      <div key={location.location_id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">{location.name}</div>
                            <div className="text-sm text-gray-600 mt-1">{location.address}</div>
                            {location.phone_number && (
                              <div className="text-sm text-gray-600 mt-1">
                                Phone: {location.country_code} {location.phone_number}
                              </div>
                            )}
                            {location.fax_number && (
                              <div className="text-sm text-gray-600">
                                Fax: {location.country_code} {location.fax_number}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteLocation(location.location_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500">No locations added yet.</p>
                  </div>
                )}

                {/* Add New Location Form */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium mb-4">Add New Location</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Location ID</Label>
                      <Input
                        value={newLocation.location_id}
                        onChange={(e) => setNewLocation({ ...newLocation, location_id: e.target.value.toLowerCase().replace(/\s/g, '') })}
                        placeholder="newyork"
                      />
                      <p className="text-sm text-gray-500 mt-1">A unique identifier (no spaces, lowercase)</p>
                    </div>
                    <div>
                      <Label>Location Name</Label>
                      <Input
                        value={newLocation.name}
                        onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                        placeholder="New York"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Address</Label>
                    <Input
                      value={newLocation.address}
                      onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                      placeholder="101 Main Street New York, NY 10001"
                    />
                  </div>

                  <div className="mt-4">
                    <Label>Address Link URL</Label>
                    <Input
                      value={newLocation.address_link}
                      onChange={(e) => setNewLocation({ ...newLocation, address_link: e.target.value })}
                      placeholder="https://maps.google.com/?q=your+address"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Enter the URL where users will be directed when they click on the address (e.g., Google Maps link). If left empty, a Google Maps link will be generated automatically.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label>Country Code</Label>
                      <Input
                        value={newLocation.country_code}
                        onChange={(e) => setNewLocation({ ...newLocation, country_code: e.target.value })}
                        placeholder="+1"
                      />
                    </div>
                    <div>
                      <Label>Phone Number</Label>
                      <Input
                        value={newLocation.phone_number}
                        onChange={(e) => setNewLocation({ ...newLocation, phone_number: formatPhoneInput(e.target.value) })}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                      />
                    </div>
                    <div>
                      <Label>Fax Number</Label>
                      <Input
                        value={newLocation.fax_number}
                        onChange={(e) => setNewLocation({ ...newLocation, fax_number: e.target.value })}
                        placeholder="+1 (555) 765-4321"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <Button onClick={handleAddLocation}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Location
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Field Controls Tab */}
          <TabsContent value="field-controls">
            <Card>
              <CardHeader>
                <CardTitle>Field Controls</CardTitle>
                <CardDescription>
                  Control which fields are editable and visible to users.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Field Editability */}
                  <div>
                    <h3 className="text-lg font-medium mb-2">Field Editability</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Control which fields users can edit. Toggle off to lock a field.
                    </p>
                    <div className="space-y-2">
                      {fields.filter(field => field !== 'profileImage').map((field) => {
                        const snakeField = toSnakeCase(field);
                        const editableKey = `editable_${snakeField}`;
                        return (
                          <div key={`editable-${field}`} className="flex items-center justify-between py-2">
                            <Label className="text-sm">{formatFieldName(field)}</Label>
                            <Switch
                              checked={config[editableKey] !== false}
                              onCheckedChange={(checked) =>
                                setConfig({ ...config, [editableKey]: checked })
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Field Visibility */}
                  <div>
                    <h3 className="text-lg font-medium mb-2">Field Visibility</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Control which fields are visible to users. Toggle off to hide a field completely.
                    </p>
                    <div className="space-y-2">
                      {fields.map((field) => {
                        const snakeField = toSnakeCase(field);
                        const visibleKey = `visible_${snakeField}`;
                        return (
                          <div key={`visible-${field}`} className="flex items-center justify-between py-2">
                            <Label className="text-sm">{formatFieldName(field)}</Label>
                            <Switch
                              checked={config[visibleKey] !== false}
                              onCheckedChange={(checked) =>
                                setConfig({ ...config, [visibleKey]: checked })
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end border-t pt-4 mt-6">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* White Label Tab */}
          <TabsContent value="white-label">
            <Card>
              <CardHeader>
                <CardTitle>White Label Header</CardTitle>
                <CardDescription>
                  Replace the SlickSignature logo with your company logo in the header.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <Label className="text-base">Enable White Label</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Replace the SlickSignature logo with your company logo in the header.
                    </p>
                  </div>
                  <Switch
                    checked={config.white_label_enabled === true}
                    onCheckedChange={(checked) =>
                      setConfig({ ...config, white_label_enabled: checked })
                    }
                  />
                </div>

                {config.white_label_enabled && (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-blue-800">
                          Your company logo will link to: <span className="font-mono">{config.website || company?.website || 'slicksignature.com'}</span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label>Header Background Color</Label>
                      <div className="flex items-center space-x-4 mt-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="header-bg"
                            value="black"
                            checked={config.header_bg_color !== 'white'}
                            onChange={() => setConfig({ ...config, header_bg_color: 'black' })}
                            className="w-4 h-4"
                          />
                          <span>Black</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="header-bg"
                            value="white"
                            checked={config.header_bg_color === 'white'}
                            onChange={() => setConfig({ ...config, header_bg_color: 'white' })}
                            className="w-4 h-4"
                          />
                          <span>White</span>
                        </label>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label className="text-base mb-3 block">Header Preview</Label>
                      <div 
                        className="rounded-lg p-4 flex items-center justify-center"
                        style={{ 
                          backgroundColor: config.header_bg_color === 'white' ? '#ffffff' : '#000000',
                          color: config.header_bg_color === 'white' ? '#000000' : '#ffffff'
                        }}
                      >
                        {config.company_logo ? (
                          <img src={config.company_logo} alt="Company Logo" className="h-12 max-w-full object-contain" />
                        ) : (
                          <div className="text-center py-4">
                            <p className="font-medium">Upload a logo in the Branding tab</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> To complete white labeling, make sure you've uploaded your company logo in the Branding tab.
                      </p>
                    </div>
                  </>
                )}

                <div className="flex justify-end border-t pt-4">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingTab companyId={user?.company_id} />
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <AccountTab userId={user?.user_id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Image Cropper Modal */}
      <ImageCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={cropperConfig.aspectRatio}
        recommendedSize={cropperConfig.recommendedSize}
        allowFreeform={cropperConfig.allowFreeform}
        title={`Crop ${cropperConfig.type === 'logo' ? 'Logo' : cropperConfig.type === 'banner' ? 'Banner' : 'Default Profile'}`}
      />
    </div>
  );
};

export default CompanySettings;
